import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  TASK_DEFAULTS,
  taskEfficiencySchema,
  type TaskEfficiencyState,
} from "./taskEfficiencySchema";

const ARRAY_KEYS = new Set([
  "groupBy",
  "requestType",
  "status",
  "client",
  "lob",
  "eng",
  "assigned",
  "country",
  "bucket",
  "taskCategory",
  "taskName",
  "actorRole",
  "taskStatus",
  "drillKeys",
  "drillVals",
  "qPills",
]);
const NUMBER_KEYS = new Set(["dateRange", "minSample", "page", "pageSize"]);
const BOOLEAN_KEYS = new Set(["rework", "onboardingsOnly", "redeployment"]);

function parseFromSearchParams(params: URLSearchParams): TaskEfficiencyState {
  const raw: Record<string, unknown> = {};
  for (const key of ARRAY_KEYS) {
    const values = params.getAll(key);
    if (values.length > 0) raw[key] = values;
  }
  for (const [key, value] of params.entries()) {
    if (ARRAY_KEYS.has(key)) continue;
    if (NUMBER_KEYS.has(key)) {
      const n = Number(value);
      if (!Number.isNaN(n)) raw[key] = n;
    } else if (BOOLEAN_KEYS.has(key)) {
      raw[key] = value === "true";
    } else {
      raw[key] = value;
    }
  }
  const result = taskEfficiencySchema.safeParse(raw);
  if (result.success) return result.data;
  if (typeof console !== "undefined") {
    console.warn("[taskEfficiency] invalid URL state:", result.error.issues);
  }
  const bad = new Set(result.error.issues.map((i) => i.path[0] as string));
  for (const k of bad) delete raw[k];
  return taskEfficiencySchema.parse(raw);
}

function serializeToSearchParams(state: TaskEfficiencyState): URLSearchParams {
  const params = new URLSearchParams();
  const def = TASK_DEFAULTS as Record<string, unknown>;
  for (const [key, value] of Object.entries(state) as [keyof TaskEfficiencyState, unknown][]) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      // Skip if the array matches the default.
      const dval = def[key] as unknown[] | undefined;
      if (
        dval &&
        dval.length === value.length &&
        dval.every((v, i) => v === (value as unknown[])[i])
      ) {
        continue;
      }
      if (value.length === 0) continue;
      for (const v of value) params.append(key, String(v));
      continue;
    }
    if (typeof value === "boolean") {
      if (value === def[key]) continue;
      params.set(key, value ? "true" : "false");
      continue;
    }
    if (def[key] === value) continue;
    if (value === "") continue;
    params.set(key, String(value));
  }
  return params;
}

export function useTaskEfficiencyUrlState(): [
  TaskEfficiencyState,
  (next: Partial<TaskEfficiencyState>) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseFromSearchParams(searchParams), [searchParams]);
  const set = useCallback(
    (next: Partial<TaskEfficiencyState>) => {
      const merged = taskEfficiencySchema.parse({ ...state, ...next });
      setSearchParams(serializeToSearchParams(merged));
    },
    [state, setSearchParams],
  );
  return [state, set];
}
